import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import arrowLandingBtn from "@/assets/images/user/arrowLandingBtn.svg";
import bgLanding from "@/assets/images/user/bgLanding.png";
import bgLandingControl from "@/assets/images/user/bgLandingControl.png";
import bgLandingM from "@/assets/images/user/bgLandingM.jpg";
import bgLandingTb from "@/assets/images/user/bgLandingTb.jpg";
import landingBtnTxt from "@/assets/images/user/landingBtnTxt.svg";
import landingBtnTxtOn from "@/assets/images/user/landingBtnTxtOn.svg";
import landingContent from "@/assets/images/user/landingContent.png";
import landingContentM from "@/assets/images/user/landingContentM.png";
import landingContentTb from "@/assets/images/user/landingContentTb.png";
import landingPlay from "@/assets/images/user/landingPlay.png";
import landingPlayLine from "@/assets/images/user/landingPlayLine.svg";
import landingTxt from "@/assets/images/user/landingTxt.svg";
import landingTxtTb from "@/assets/images/user/landingTxtTb.png";
import Footer from "@/components/user/layout/Footer";
import { usePopupStore } from "@/store/common/usePopupStore";

// 이미지 프리로딩 함수
const preloadImage = (src: string) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = resolve;
        img.onerror = reject;
    });
};

// 스켈레톤 UI 컴포넌트
interface ImageSkeletonProps {
    className?: string;
}

const ImageSkeleton = ({ className }: ImageSkeletonProps) => (
    <div className={`animate-pulse bg-gray-200 w-full h-full ${className || ""}`} />
);

export default function Landing() {
    const { setLoadingPop, setConfirmPop } = usePopupStore();
    const [isLoading, setIsLoading] = useState(true);

    // 이미지 프리로딩
    useEffect(() => {
        const images = [
            bgLandingM,
            bgLandingTb,
            bgLanding,
            bgLandingControl,
            landingPlay,
            landingTxt,
            landingBtnTxt,
            landingBtnTxtOn,
            landingPlayLine,
            landingContentM,
            landingContentTb,
            landingContent,
        ];

        Promise.all(images.map(preloadImage))
            .then(() => {
                setIsLoading(false);
            })
            .catch(error => {
                console.error("이미지 로딩 실패:", error);
                setIsLoading(false);
            });
    }, []);

    // 로딩팝업
    useEffect(() => {
        setLoadingPop(isLoading);
        return () => setLoadingPop(false);
    }, [isLoading, setLoadingPop]);

    // 이미지 로딩중일때
    if (isLoading) {
        return (
            <div className="min-h-screen">
                <div className="max-w-[1488px] mx-auto px-[16px] relative z-[1] md:px-[30px]">
                    <div className="max-w-[1200px] mx-auto">
                        <div className="flex flex-col gap-y-[20px] -mt-[20px] md:mt-[40px] md:gap-y-[40px] md:px-[10px] xl:-mt-[80px] xl:gap-y-[40px]">
                            <ImageSkeleton className="h-[200px] md:h-[300px] xl:h-[400px]" />
                            <ImageSkeleton className="h-[100px]" />
                        </div>
                    </div>
                    <div className="p-[80px_0_40px] flex justify-center md:pt-[100px] xl:p-[120px_0_60px]">
                        <ImageSkeleton className="h-[400px] md:h-[600px] xl:h-[800px]" />
                    </div>
                </div>
            </div>
        );
    }

    // 참가신청 버튼 클릭 시
    const handleClickApplyBtn = () => {
        const openDate = new Date("2025-06-09");
        const currentDate = new Date();

        if (currentDate >= openDate) {
            window.open("https://forms.gle/XVuBV7Kv9V3HUT926", "_blank");
        } else {
            setConfirmPop(true, "6월 9일에 오픈됩니다!", 1);
        }
    };

    return (
        <div className="min-w-[375px]">
            <div className="relative">
                <div className="absolute w-full h-full -z-[1]">
                    <img
                        src={bgLandingM}
                        alt="배경이미지"
                        className="w-full h-full object-cover md:hidden xl:hidden"
                        loading="lazy"
                    />
                    <img
                        src={bgLandingTb}
                        alt="배경이미지"
                        className="w-full h-full object-cover hidden md:block xl:hidden"
                        loading="lazy"
                    />
                    <img
                        src={bgLanding}
                        alt="배경이미지"
                        className="w-full h-full object-cover hidden xl:block"
                        loading="lazy"
                    />
                </div>
                <div className="flex justify-center relative">
                    <img src={bgLandingControl} alt="컨트롤" loading="lazy" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100px] md:w-[30%] xl:w-auto">
                        <img src={landingPlay} alt="재생버튼" className="animate-bounce-landing" loading="lazy" />
                    </div>
                </div>
                <div className="max-w-[1488px] mx-auto px-[16px] relative z-[1] md:px-[30px]">
                    <div className="max-w-[1200px] mx-auto">
                        <div className="flex flex-col gap-y-[20px] -mt-[20px] md:mt-[40px] md:gap-y-[40px] md:px-[10px] xl:-mt-[80px] xl:gap-y-[40px]">
                            <div className="flex justify-center xl:justify-between xl:items-end">
                                <img
                                    src={landingTxt}
                                    alt="GXG 2025 게임음악 경연대회"
                                    className="hidden md:hidden xl:block"
                                    loading="lazy"
                                />
                                <img
                                    src={landingTxtTb}
                                    alt="GXG 2025 게임음악 경연대회"
                                    className="block w-[365.5px] md:w-auto xl:hidden"
                                    loading="lazy"
                                />
                                <button
                                    type="button"
                                    onClick={handleClickApplyBtn}
                                    className="relative hidden box-border flex-col items-center gap-[12px] pt-[48px] size-[280px] rounded-full bg-multi-gradient group after:absolute after:content-[''] after:rounded-full after:size-full after:inset-0 after:border-[8px] after:border-[#444444] after:opacity-0 hover:border hover:border-[#4021F6] hover:[box-shadow:0_0_20px_0_#A494FF,0_0_20px_0_#2E16BE] hover:after:opacity-100 hover:gap-[24px] xl:flex"
                                >
                                    <div className="size-[60px] overflow-hidden flex flex-col">
                                        <img
                                            src={arrowLandingBtn}
                                            alt="화살표"
                                            className="transition-all duration-500"
                                            loading="lazy"
                                        />
                                        <img src={arrowLandingBtn} alt="화살표" loading="lazy" />
                                    </div>
                                    <div className="relative w-full flex flex-col items-center justify-start">
                                        <img
                                            src={landingBtnTxt}
                                            alt="참가신청 바로가기"
                                            className="opacity-100 transition-all duration-300 group-hover:opacity-0"
                                            loading="lazy"
                                        />
                                        <img
                                            src={landingBtnTxtOn}
                                            alt="참가신청 바로가기"
                                            className="absolute opacity-0 transition-all duration-300 group-hover:opacity-100"
                                            loading="lazy"
                                        />
                                    </div>
                                </button>
                            </div>
                            <img src={landingPlayLine} alt="재생라인" loading="lazy" />
                        </div>
                    </div>
                    <div className="p-[80px_0_40px] flex justify-center md:pt-[100px] xl:p-[120px_0_60px]">
                        <img src={landingContentM} alt="컨텐츠" className="md:hidden xl:hidden" loading="lazy" />
                        <img src={landingContentTb} alt="컨텐츠" className="hidden md:block xl:hidden" loading="lazy" />
                        <img src={landingContent} alt="컨텐츠" className="hidden xl:block" loading="lazy" />
                    </div>
                    <ul className="flex flex-col gap-[12px] pb-[100px] xl:pb-[160px] xl:flex-row xl:justify-between xl:gap-[24px]">
                        <li className="flex-1">
                            <Link
                                to={`https://www.youtube.com/@GXG_gameculture`}
                                target="_blank"
                                className="relative block w-full h-[60px] text-primary text-[22px] font-[500] text-center leading-[60px] font-['GongGothicMedium'] clip-path-octagon border border-primary xl:h-[72px] xl:leading-[72px]"
                            >
                                <span>2024 경연 무대 보러가기</span>
                                <span className="absolute left-0 top-0 border-[8px] border-primary border-b-transparent border-r-transparent" />
                                <span className="absolute left-0 bottom-0 border-[8px] border-primary border-t-transparent border-r-transparent" />
                                <span className="absolute right-0 top-0 border-[8px] border-primary border-b-transparent border-l-transparent" />
                                <span className="absolute right-0 bottom-0 border-[8px] border-primary border-t-transparent border-l-transparent" />
                            </Link>
                        </li>
                        <li className="flex-1">
                            <Link
                                to={`/community/faq`}
                                target="_blank"
                                className="relative block w-full h-[60px] text-white text-[22px] font-[500] text-center leading-[60px] font-['GongGothicMedium'] clip-path-octagon border border-white xl:h-[72px] xl:leading-[72px]"
                            >
                                <span>자주 묻는 질문</span>
                                <span className="absolute left-0 top-0 border-[8px] border-white border-b-transparent border-r-transparent" />
                                <span className="absolute left-0 bottom-0 border-[8px] border-white border-t-transparent border-r-transparent" />
                                <span className="absolute right-0 top-0 border-[8px] border-white border-b-transparent border-l-transparent" />
                                <span className="absolute right-0 bottom-0 border-[8px] border-white border-t-transparent border-l-transparent" />
                            </Link>
                        </li>
                        <li className="flex-1">
                            <button
                                type="button"
                                onClick={handleClickApplyBtn}
                                className="relative block w-full h-[60px] text-white text-[22px] font-[500] text-center leading-[60px] font-['GongGothicMedium'] clip-path-octagon border border-primary bg-primary xl:h-[72px] xl:leading-[72px]"
                            >
                                <span>게임음악 경연대회 참가 신청</span>
                                <span className="absolute left-0 top-0 border-[8px] border-primary border-b-transparent border-r-transparent" />
                                <span className="absolute left-0 bottom-0 border-[8px] border-primary border-t-transparent border-r-transparent" />
                                <span className="absolute right-0 top-0 border-[8px] border-primary border-b-transparent border-l-transparent" />
                                <span className="absolute right-0 bottom-0 border-[8px] border-primary border-t-transparent border-l-transparent" />
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
            <Footer />
        </div>
    );
}
